import type { Worksheet } from "exceljs";
import { DateTime } from "luxon";
import { isOvernightShiftStartHour, parseTimeString } from "../utils/dateUtils";
import {
	type BreakRulesConfig,
	type CensusPointInTimes,
	type DailyPrintoutGrouping,
	DailyPrintoutGroupingOption,
	type DepartmentLocation,
	type EmploymentType,
	type HospitalData,
	type Nurse,
	type NurseFullRole,
	type NurseInShift,
	type NurseRole,
	NurseTrainingStatus,
} from "../utils/models";
import { getEffectiveAmountRequired } from "../utils/ratioLadder";
import { buildCanonicalKeyMap } from "../utils/timeGrouping";
import { getShiftCensus, getShiftWorkHours, inverseRolesMap, shouldApplyNurseNotOnboardedVisibleFlag } from "../utils/utilFunctions";
import { censusPointInTimeRows } from "./dailyPrintout/util/censusPointInTimes";

// The gap finder and its time-slot helpers used to live here as a full second copy of
// `dailyPrintout/phases/gapFinder.ts`. Two implementations meant the on-screen Daily Gaps view and
// the printout could answer "is this slot covered?" differently, so the duplicate was deleted and
// every caller now imports the canonical one. `TimeSlot` is re-exported for existing importers.
export type { TimeSlot } from "./dailyPrintout/types";
// Imported from the single source of truth (see top of file) and re-exported so existing importers keep working.
export { getEffectiveAmountRequired };

// Given time windows sorted by count descending, assigns each to the first existing canonical group
// using the shared proximity/containment merge logic from timeGrouping.
// Returns a map from each window's key to its canonical group key.
// Uses absolute minutes (from epoch) so that midnight-crossing shifts compare correctly.
const buildCanonicalKeyMapFromDateTimes = (sortedWindows: ReadonlyArray<{ key: string; start: DateTime; end: DateTime }>): Map<string, string> => {
	const asMinutes = sortedWindows.map(({ key, start, end }) => ({
		key,
		startMin: Math.floor(start.toMillis() / 60_000),
		endMin: Math.floor(end.toMillis() / 60_000),
	}));
	return buildCanonicalKeyMap(asMinutes);
};

/**
 * Summary-mode-only printout options.
 *
 * The census block (Census / Nursing Hours / HPPD) mirrors the on-screen Day View stats (`dayStats`) so the
 * summary printout always matches what the user sees on screen. `dayViewHppd` is the standard HPPD (excludes
 * PPD-excluded staff & trainees — i.e. the NHPPD calc) and is shown as the default "HPPD" row. When a toggle
 * is on, the row is replaced by the precomputed `dhppd`/`nhppd` variants (which honor the include-trainees toggle).
 */
export interface SummaryPrintoutOptions {
	showDhppd: boolean;
	showNhppd: boolean;
	showActualStaffColumn: boolean;
	showActualHoursColumn: boolean;
	dhppd: number;
	nhppd: number;
	// Day View stats (mirrored into the census block)
	dayViewCensus: number;
	dayViewNursingHours: number;
	dayViewHppd: number;
	/** CDPH §72329.2 readings appended to the census block; undefined leaves the block as it is today. */
	censusPointInTimes: CensusPointInTimes | undefined;
}

export async function createDayViewWithoutFloorsSummary(
	worksheet: Worksheet,
	hospitalData: any,
	weekIndex: any,
	dayIndex: any,
	department: any,
	day: any,
	date: any,
	printNotes: boolean,
	grouping: DailyPrintoutGrouping = [],
	summaryOptions: SummaryPrintoutOptions,
	jobManagementEnabled: boolean,
) {
	const shiftToNurseMap = arrangeNursesData(hospitalData, weekIndex, dayIndex, department.department_id);

	// Basic setup
	setColumnWidths(worksheet, printNotes);
	addTitle(worksheet, "A1", "D1", `${department.display_name} - DAILY SCHEDULE`);

	// Setup A4 page settings for summary view. Up to 2 optional "Actual" columns widen the table beyond D/E,
	// so the print area's last column is resolved from the actual column count below.
	const tableColumnCount = 3 + (summaryOptions.showActualStaffColumn ? 1 : 0) + (summaryOptions.showActualHoursColumn ? 1 : 0);
	const lastColNumber = Math.max(printNotes ? 5 : 4, tableColumnCount);
	const maxCol = numberToColumnLetter(lastColNumber);
	worksheet.pageSetup = {
		paperSize: 9, // A4
		orientation: "portrait",
		fitToPage: true,
		fitToWidth: 1,
		fitToHeight: 0,
		margins: {
			left: 0.4,
			right: 0.4,
			top: 0.6,
			bottom: 0.6,
			header: 0.3,
			footer: 0.3,
		},
		horizontalCentered: true,
		printArea: `A1:${maxCol}100`,
	};

	worksheet.headerFooter = {
		oddHeader: `&C&B${department.display_name} - Summary&R${date.toFormat ? date.toFormat("yyyy-MM-dd") : date}`,
		oddFooter: "&LPrinted: &D &T&CPage &P of &N",
	};

	// Top section
	addDayAndDateFields(worksheet, day, date);
	const lastCensusBlockRow = addCensusAndStaffingData(worksheet, summaryOptions);

	// Start the staffing table below the census block, preserving a one-row spacer (min row 7 keeps the
	// original layout when no extra HPPD or CDPH rows are rendered).
	const tableStartRow = Math.max(7, lastCensusBlockRow + 2);
	const groupByTime = grouping.includes(DailyPrintoutGroupingOption.START_TIME);
	addStaffingSummaryTableNoFloors(
		worksheet,
		tableStartRow,
		shiftToNurseMap,
		department.department_id,
		department.breakRulesConfig ?? [],
		groupByTime,
		summaryOptions,
		jobManagementEnabled,
	);
}

/** 1-based column index → Excel letter (1→A, 27→AA). Small helper for the summary's dynamic column count. */
function numberToColumnLetter(col: number): string {
	let result = "";
	let n = col;
	while (n > 0) {
		const rem = (n - 1) % 26;
		result = String.fromCharCode(65 + rem) + result;
		n = Math.floor((n - 1) / 26);
	}
	return result;
}

type FlatNurseEntry = {
	role: string;
	startTime: DateTime;
	endTime: DateTime;
	hours: number;
	departmentId: string;
	nurseRole: NurseRole;
	nurseType: EmploymentType;
	jobId: string | null;
	/** Populated from the roster in `arrangeNursesData`; "" = non-union. */
	union: string;
};

const flattenNursesFromShiftMap = (shiftToNurseMap: any): FlatNurseEntry[] => {
	const nurses: FlatNurseEntry[] = [];
	for (const shiftData of Object.values(shiftToNurseMap)) {
		if (!shiftData) continue;
		for (const [key, nurseData] of Object.entries(shiftData as Record<string, any>)) {
			if (key !== "census" && Array.isArray(nurseData)) {
				for (const nurse of nurseData) {
					if (typeof nurse.hours === "number" && DateTime.isDateTime(nurse.startTime)) {
						nurses.push({
							role: key,
							startTime: nurse.startTime,
							endTime: nurse.startTime.plus({ hours: nurse.hours }),
							hours: nurse.hours,
							departmentId: nurse.departmentId,
							nurseRole: nurse.role,
							nurseType: nurse.type,
							jobId: nurse.jobId ?? null,
							union: nurse.union ?? "",
						});
					}
				}
			}
		}
	}
	return nurses;
};

const formatTimeForSummary = (time: DateTime): string => {
	const hour = time.hour;
	const minutes = time.minute;
	const h = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
	const minStr = minutes !== 0 ? `:${minutes.toString().padStart(2, "0")}` : "";
	const ampm = hour >= 12 ? "PM" : "AM";
	return `${h}${minStr} ${ampm}`;
};

const groupNursesByTime = (nurses: FlatNurseEntry[]): Map<string, { label: string; nurses: FlatNurseEntry[]; start: DateTime; end: DateTime }> => {
	// Build exact-time buckets
	const buckets = new Map<string, { nurses: FlatNurseEntry[]; start: DateTime; end: DateTime }>();
	for (const nurse of nurses) {
		const key = `${nurse.startTime.hour.toString().padStart(2, "0")}:${nurse.startTime.minute.toString().padStart(2, "0")}-${nurse.endTime.hour.toString().padStart(2, "0")}:${nurse.endTime.minute.toString().padStart(2, "0")}`;
		const existing = buckets.get(key);
		if (existing) {
			existing.nurses.push(nurse);
		} else {
			buckets.set(key, { nurses: [nurse], start: nurse.startTime, end: nurse.endTime });
		}
	}

	// Use the same canonical time grouping as the detailed view
	const sortedBuckets = [...buckets.entries()].sort((a, b) => b[1].nurses.length - a[1].nurses.length).map(([key, { start, end }]) => ({ key, start, end }));
	const bucketToCanonical = buildCanonicalKeyMapFromDateTimes(sortedBuckets);

	// Merge into canonical groups
	const groups = new Map<string, { label: string; nurses: FlatNurseEntry[]; start: DateTime; end: DateTime }>();
	for (const [exactKey, { nurses: bucketNurses, start, end }] of buckets) {
		const canonicalKey = bucketToCanonical.get(exactKey) ?? exactKey;
		const existing = groups.get(canonicalKey);
		if (existing) {
			existing.nurses.push(...bucketNurses);
		} else {
			const label = `${formatTimeForSummary(start)} - ${formatTimeForSummary(end)}`;
			groups.set(canonicalKey, { label, nurses: [...bucketNurses], start, end });
		}
	}

	return groups;
};

const sortTimeGroups = (groups: Map<string, { label: string; nurses: FlatNurseEntry[]; start: DateTime; end: DateTime }>) => {
	return [...groups.values()].sort((a, b) => {
		const startDiff = a.start.toMillis() - b.start.toMillis();
		if (startDiff !== 0) return startDiff;
		return a.end.toMillis() - b.end.toMillis();
	});
};

function addStaffingSummaryTableNoFloors(
	worksheet: Worksheet,
	startRow: number,
	shiftToNurseMap: any,
	departmentId: string,
	breakRulesConfig: BreakRulesConfig[],
	groupByTime: boolean,
	summaryOptions: SummaryPrintoutOptions,
	jobManagementEnabled: boolean,
) {
	const allNurses = flattenNursesFromShiftMap(shiftToNurseMap);

	// Ordered column descriptors. The optional "Actual" columns sit next to their base column and are left
	// blank (bordered) for hand-write-in. Everything below addresses cells by numeric index so inserting a
	// column never breaks a hardcoded A/B/C reference.
	type SummaryColKey = "role" | "staff" | "actualStaff" | "hours" | "actualHours";
	const cols: { key: SummaryColKey; header: string; width: number }[] = [
		{ key: "role", header: "Role", width: 25 },
		{ key: "staff", header: "Number Of Staff", width: 15 },
		...(summaryOptions.showActualStaffColumn ? [{ key: "actualStaff" as const, header: "Actual Number Of Staff", width: 20 }] : []),
		{ key: "hours", header: "Total Hours Scheduled", width: 20 },
		...(summaryOptions.showActualHoursColumn ? [{ key: "actualHours" as const, header: "Actual Total Hours", width: 18 }] : []),
	];
	const staffCol = cols.findIndex((c) => c.key === "staff") + 1;
	const hoursCol = cols.findIndex((c) => c.key === "hours") + 1;
	const lastCol = cols.length;

	// Set column widths for the summary table
	cols.forEach((c, i) => {
		worksheet.getColumn(i + 1).width = c.width;
	});

	const setCellBorder = (r: number, c: number, top: "thin" | "double", bottom: "thin" | "double") => {
		worksheet.getCell(r, c).border = { top: { style: top }, left: { style: "thin" }, bottom: { style: bottom }, right: { style: "thin" } };
	};
	const borderRow = (r: number, top: "thin" | "double", bottom: "thin" | "double") => {
		for (let c = 1; c <= lastCol; c++) setCellBorder(r, c, top, bottom);
	};

	const addTableHeaders = (currentRow: number) => {
		cols.forEach((c, i) => {
			const cell = worksheet.getCell(currentRow, i + 1);
			cell.value = c.header;
			cell.font = { bold: true };
		});
		borderRow(currentRow, "thin", "thin");
	};

	const addRoleRows = (nurses: FlatNurseEntry[], currentRow: number): { row: number; staff: number; hours: number } => {
		const roleAgg: Record<string, { count: number; hours: number }> = {};
		for (const nurse of nurses) {
			const workHours = getShiftWorkHours(
				departmentId,
				nurse.endTime,
				nurse.startTime,
				breakRulesConfig,
				nurse.nurseRole,
				nurse.nurseType,
				nurse.jobId ? [nurse.jobId] : [],
				jobManagementEnabled,
				nurse.union,
			);
			if (!roleAgg[nurse.role]) {
				roleAgg[nurse.role] = { count: 0, hours: 0 };
			}
			roleAgg[nurse.role].count += 1;
			roleAgg[nurse.role].hours += workHours;
		}

		let totalStaff = 0;
		let totalHours = 0;
		let row = currentRow;
		const sortedRoles = Object.entries(roleAgg).sort((a, b) => a[0].localeCompare(b[0]));
		for (const [role, data] of sortedRoles) {
			worksheet.getCell(row, 1).value = role;
			worksheet.getCell(row, staffCol).value = data.count;
			worksheet.getCell(row, hoursCol).value = Number.isInteger(data.hours) ? data.hours : Number(data.hours.toFixed(2));
			// Actual columns (if any) stay empty but bordered.
			borderRow(row, "thin", "thin");
			totalStaff += data.count;
			totalHours += data.hours;
			row++;
		}
		return { row, staff: totalStaff, hours: totalHours };
	};

	const addTotalRow = (currentRow: number, totalStaff: number, totalHours: number) => {
		worksheet.getCell(currentRow, 1).value = "TOTAL";
		worksheet.getCell(currentRow, staffCol).value = totalStaff;
		worksheet.getCell(currentRow, hoursCol).value = Number.isInteger(totalHours) ? totalHours : Number(totalHours.toFixed(2));
		for (let c = 1; c <= lastCol; c++) worksheet.getCell(currentRow, c).font = { bold: true };
		borderRow(currentRow, "double", "double");
	};

	if (!groupByTime) {
		// Flat role summary (original behavior)
		addTableHeaders(startRow);
		const { row, staff, hours } = addRoleRows(allNurses, startRow + 1);
		addTotalRow(row + 1, staff, hours);
		return;
	}

	// Group by time
	const timeGroups = groupNursesByTime(allNurses);
	const sortedGroups = sortTimeGroups(timeGroups);

	let row = startRow;
	let totalStaff = 0;
	let totalHours = 0;

	for (const group of sortedGroups) {
		// Time group header
		worksheet.getCell(row, 1).value = group.label;
		worksheet.mergeCells(row, 1, row, lastCol);
		worksheet.getCell(row, 1).font = { bold: true, underline: true };
		row++;

		// Column headers for this group
		addTableHeaders(row);
		row++;

		const result = addRoleRows(group.nurses, row);
		row = result.row;
		totalStaff += result.staff;
		totalHours += result.hours;

		// Empty row between groups
		row++;
	}

	addTotalRow(row, totalStaff, totalHours);
}

const arrangeNursesData = (hospitalData: HospitalData, weekIndex: number, dayIndex: number, department_id: string) => {
	// Create dictionary mapping shift details to nurse names
	const shiftToNurseMap: any = {};

	hospitalData.weeks[weekIndex].weeklyShifts.forEach((shift, index) => {
		shiftToNurseMap[index] = { census: 0 };
	});

	const shiftTypeToIndex = new Map(hospitalData.weeks[weekIndex].weeklyShifts.map((shift, index) => [shift.type, index]));

	hospitalData.weeks[weekIndex].weeklyShifts.forEach((shift) => {
		// Calculate census for each shift
		const census = getShiftCensus(hospitalData.weeks[weekIndex], shiftTypeToIndex.get(shift.type) as number, dayIndex);
		const shiftIndex = shiftTypeToIndex.get(shift.type) as number;
		shiftToNurseMap[shiftIndex].census = census;

		shift.days[dayIndex].nurses.forEach((nurse: NurseInShift) => {
			const fullNurse: Nurse | undefined = hospitalData.nurses.find((n: Nurse) => n.id === nurse.nurseId);
			if (fullNurse === undefined || nurse.departmentId !== department_id) return;

			const role = inverseRolesMap[fullNurse.role];

			const employmentType = fullNurse.employmentType
				.split(/(?=[A-Z])/)
				.map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
				.join(" ");

			const convertShift = (time: DateTime) => {
				const hour = time.hour;
				const minutes = time.minute;
				return `${hour > 12 ? hour - 12 : hour}${minutes !== 0 ? `:${minutes.toString().padStart(2, "0")}` : ""}${hour >= 12 ? "pm" : "am"}`;
			};

			// Determine if this is a charge nurse
			let shiftKey: string;
			if (nurse.charge) {
				shiftKey = "CHARGE";
			} else {
				// Use role as the key
				shiftKey = inverseRolesMap[fullNurse.role];
			}

			if (!shiftToNurseMap[shiftIndex][shiftKey]) {
				shiftToNurseMap[shiftIndex][shiftKey] = [];
			}

			const hours = nurse.endTime.diff(nurse.startTime, "hours").as("hours"); // true for floating point
			let notes =
				nurse.trainingStatus === NurseTrainingStatus.Trainee
					? "Trainee"
					: nurse.trainingStatus === NurseTrainingStatus.Education
						? "Education"
						: nurse.trainingStatus === NurseTrainingStatus.Orientation
							? "Orientation"
							: "";
			if (nurse.notes) {
				notes += notes ? `; ${nurse.notes}` : nurse.notes;
			}
			if (shouldApplyNurseNotOnboardedVisibleFlag(fullNurse)) {
				notes += `${notes ? "; " : ""}Not Onboarded`;
			}

			shiftToNurseMap[shiftIndex][shiftKey].push({
				employmentType: employmentType,
				role: role,
				type: nurse.type,
				notes: notes,
				hours: hours,
				shift: `${convertShift(nurse.startTime)}-${convertShift(nurse.endTime)}`,
				startTime: nurse.startTime,
				location: nurse.location,
				union: fullNurse.union ?? "",
			});
		});
	});

	// Sort nurses within each shift by start time (reverse order for night shift)
	hospitalData.weeks[weekIndex].weeklyShifts.forEach((shift, shiftIndex) => {
		Object.keys(shiftToNurseMap[shiftIndex]).forEach((shiftKey) => {
			if (Array.isArray(shiftToNurseMap[shiftIndex][shiftKey])) {
				shiftToNurseMap[shiftIndex][shiftKey].sort((a: any, b: any) => {
					// Night shift (index 1) should be sorted in reverse
					const sortOrder = shiftIndex === 1 ? -1 : 1;
					return sortOrder * (new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
				});
			}
		});
	});

	return shiftToNurseMap;
};

const setColumnWidths = (worksheet: Worksheet, printNotes: boolean): void => {
	// We can't mmodify worksheet.columns, so we must define it before
	const cols = [
		{ width: 21 }, // Column A - assignments
		{ width: 23 }, // Column B - Names
		{ width: 5 }, // Column C - Role
		{ width: 9 }, // Column D - employment type
	];

	if (printNotes) {
		cols.push({ width: 20 }); // Column E - Notes
	}

	worksheet.columns = cols;
};

const addTitle = (worksheet: Worksheet, start_cell: string, end_cell: string, title: string): void => {
	worksheet.mergeCells(`${start_cell}:${end_cell}`);
	const titleCell = worksheet.getCell(start_cell);
	titleCell.value = title;
	titleCell.alignment = { horizontal: "left" };
	titleCell.font = { bold: true, size: 24 };
};

const addDayAndDateFields = (worksheet: Worksheet, day: string | undefined, date: DateTime): void => {
	// Create separate cells for DAY and DATE
	const dateCell = worksheet.getCell("A2");
	const dayCell = worksheet.getCell("B2");

	// Set values
	dateCell.value = `${date.toFormat("yyyy-dd-MM")}`;
	dayCell.value = `${date.toFormat("cccc")}`;

	// Add borders
	[dayCell, dateCell].forEach((cell) => {
		cell.border = {
			top: { style: "thin" },
			left: { style: "thin" },
			bottom: { style: "thin" },
			right: { style: "thin" },
		};
		cell.font = { bold: true };
	});
};

/**
 * The summary printout's census plate, returning its last row so the caller can place the staffing
 * table under it. Exported so the plate's row arithmetic — which varies with the HPPD toggles AND the
 * CDPH readings — can be asserted without assembling a whole HospitalData fixture.
 */
export const addCensusAndStaffingData = (worksheet: Worksheet, summaryOptions: SummaryPrintoutOptions): number => {
	// Census / nursing hours occupy rows 2-3; row 4 is the HPPD row whose label depends on which HPPD
	// variant(s) the user enabled ("replace the HPPD naming with the relevant naming"). All three values
	// mirror the on-screen Day View stats so the printout matches what the user sees.
	worksheet.getCell("C2").value = "Census";
	worksheet.getCell("C3").value = "Nursing Hours";

	const formatHppd = (value: number) => (Number.isInteger(value) ? value : Number(value.toFixed(2)));

	// Census + Nursing Hours come straight from Day View stats (keeps HPPD == NursingHours / Census).
	worksheet.getCell("D2").value = summaryOptions.dayViewCensus;
	worksheet.getCell("D3").value = formatHppd(summaryOptions.dayViewNursingHours);

	// Build the HPPD row(s). Neither toggle → the default "HPPD" row shows the Day View HPPD (NHPPD calc).
	// A toggle on → replace it with the specific metric(s), using the precomputed dhppd/nhppd values.
	const hppdRows: { label: string; value: number }[] = [];
	if (!summaryOptions.showDhppd && !summaryOptions.showNhppd) {
		hppdRows.push({ label: "HPPD", value: summaryOptions.dayViewHppd });
	} else {
		if (summaryOptions.showDhppd) hppdRows.push({ label: "DHPPD", value: summaryOptions.dhppd });
		if (summaryOptions.showNhppd) hppdRows.push({ label: "NHPPD", value: summaryOptions.nhppd });
	}

	let row = 4;
	for (const hppdRow of hppdRows) {
		worksheet.getCell(`C${row}`).value = hppdRow.label;
		worksheet.getCell(`D${row}`).value = hppdRow.value;
		row++;
	}

	// The CDPH §72329.2 readings extend the same plate: label in C, value in D, so they inherit its
	// borders and column widths below. Appended rather than replacing anything — the Census row above
	// stays the 23:59 figure the rest of the printout (and HPPD) is computed from.
	for (const pointInTimeRow of censusPointInTimeRows(summaryOptions.censusPointInTimes)) {
		worksheet.getCell(`C${row}`).value = pointInTimeRow.label;
		worksheet.getCell(`D${row}`).value = pointInTimeRow.value;
		row++;
	}
	const lastCensusRow = row - 1;

	// Add borders to all census cells and make them wider
	["C", "D"].forEach((col) => {
		for (let number = 2; number <= lastCensusRow; number++) {
			worksheet.getCell(`${col}${number}`).border = {
				top: { style: "thin" },
				left: { style: "thin" },
				bottom: { style: "thin" },
				right: { style: "thin" },
			};
		}
	});

	// Make columns wider for census data. The CDPH labels are longer than the HPPD ones, so the label
	// column widens only when they are present — a sheet without them keeps its current width.
	worksheet.getColumn("C").width = censusPointInTimeRows(summaryOptions.censusPointInTimes).length > 0 ? 24 : 20;
	worksheet.getColumn("D").width = 15;

	return lastCensusRow;
};
